import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

import { cn } from '../utilitarios/cn'
import { Girador } from './girador'

export type VarianteBotao =
  | 'primario'
  | 'secundario'
  | 'contorno'
  | 'fantasma'
  | 'perigo'
  | 'sucesso'
  | 'whatsapp'

export type TamanhoBotao = 'sm' | 'md' | 'lg' | 'icone'

const VARIANTES: Record<VarianteBotao, string> = {
  primario:
    'bg-marca-500 text-white hover:bg-marca-600 active:bg-marca-700 border border-transparent',
  secundario:
    'bg-superficie-3 text-conteudo hover:bg-borda border border-transparent',
  contorno:
    'bg-superficie text-conteudo border border-borda-forte hover:bg-superficie-2',
  fantasma:
    'bg-transparent text-conteudo-suave hover:bg-superficie-3 hover:text-conteudo border border-transparent',
  perigo: 'bg-perigo text-white hover:brightness-95 border border-transparent',
  sucesso: 'bg-sucesso text-white hover:brightness-95 border border-transparent',
  whatsapp: 'bg-whatsapp text-white hover:brightness-95 border border-transparent',
}

const TAMANHOS: Record<TamanhoBotao, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-base gap-2',
  icone: 'h-10 w-10 p-0 text-sm',
}

export interface PropriedadesBotao extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: VarianteBotao
  tamanho?: TamanhoBotao
  /** Desabilita e troca o conteúdo por um girador, preservando a largura. */
  carregando?: boolean
  iconeEsquerda?: ReactNode
  iconeDireita?: ReactNode
  larguraTotal?: boolean
}

/**
 * Botão do design system.
 *
 * `carregando` **também** desabilita: um botão de submit que continua clicável
 * durante a requisição é a origem mais comum de interação duplicada — e em
 * `POST /leads/{uuid}/interacoes` isso viraria duas linhas numa timeline que
 * ninguém pode apagar (RN-25).
 */
export const Botao = forwardRef<HTMLButtonElement, PropriedadesBotao>(function Botao(
  {
    variante = 'primario',
    tamanho = 'md',
    carregando = false,
    iconeEsquerda,
    iconeDireita,
    larguraTotal = false,
    className,
    children,
    disabled,
    type = 'button',
    ...resto
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled ?? carregando}
      aria-busy={carregando || undefined}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium',
        'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-marca-500/45',
        'disabled:cursor-not-allowed disabled:opacity-55',
        VARIANTES[variante],
        TAMANHOS[tamanho],
        larguraTotal && 'w-full',
        className,
      )}
      {...resto}
    >
      {carregando ? <Girador tamanho="sm" /> : iconeEsquerda}
      {tamanho === 'icone' ? (carregando ? null : children) : children}
      {!carregando && iconeDireita}
    </button>
  )
})
